import React, { useMemo, useState } from 'react'
import {
	Avatar,
	Box,
	Checkbox,
	Dialog,
	FormControlLabel,
	Tab,
	Tabs,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import { type GridColDef, type GridRowSelectionModel } from '@mui/x-data-grid-pro'
import { useModal } from '../../../../../store/ModalProvider'
import CustomButton from '../../CustomButton/CustomButton'
import { AnimatedDataGridPro } from '../../CustomDataGrid'
import { dataGridDensityTransitionSx } from '../../CustomDataGrid/dataGridDensityTransitionSx'
import CustomColumnMenu, { columnMenuPopperSx } from '../../CustomDataGrid/CustomColumnMenu'
import { popupActionButtonSx, popupSecondaryButtonSx } from '../popupButtonStyles'
import { popupInputLabelSx } from '../popupInputStyles'
import type { TimesheetNoticesModalData, TimesheetNoticeRow } from '../../../../../types/modal'

type DensityMode = 'compact' | 'standard'

const dialogShadow =
	'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)'

const checkboxSx = {
	color: '#8695AF',
	'&.Mui-checked': {
		color: '#00A5FF',
	},
	'&.MuiCheckbox-indeterminate': {
		color: '#00A5FF',
	},
}

const checkboxLabelSx = {
	margin: 0,
	'.MuiTypography-root': {
		fontFamily: 'Poppins, sans-serif',
		fontSize: '13px',
		fontWeight: 500,
		lineHeight: 1.3,
		color: '#092056',
	},
}

const timeFieldSx = {
	'& .MuiInputLabel-root': {
		...popupInputLabelSx,
	},
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#F5F7FC',
		borderRadius: '4px',
		minHeight: '40px',
		fontSize: '13px',
		fontWeight: 500,
		color: 'rgba(9, 32, 86, 0.3)',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0)',
		},
	},
	'& .MuiOutlinedInput-input': {
		padding: '8px 12px',
		lineHeight: 1.3,
	},
}

const toggleGroupSx = {
	border: '1px solid rgba(0, 165, 255, 0.3)',
	borderRadius: '6px',
	height: '24px',
	'& .MuiToggleButton-root': {
		border: 'none',
		padding: '2px',
		width: '24px',
		height: '24px',
		color: '#00A5FF',
		'&.Mui-selected': {
			backgroundColor: 'rgba(0, 165, 255, 0.15)',
			color: '#00A5FF',
		},
		'&:hover': {
			backgroundColor: 'rgba(0, 165, 255, 0.08)',
		},
	},
}

const tableSx = {
	border: 'none',
	'--DataGrid-rowBorderColor': 'transparent',
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#FFFFFF',
		borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
		borderTop: 'none',
		boxShadow: 'none',
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		fontFamily: 'Poppins, sans-serif',
		fontWeight: 600,
		fontSize: '12px',
		color: '#8695AF',
		letterSpacing: '0.17px',
		lineHeight: '24px',
		textTransform: 'none',
	},
	'& .MuiDataGrid-columnSeparator': {
		opacity: 0,
		color: 'rgba(134, 149, 175, 0.6)',
		transition: 'opacity 0.2s ease',
	},
	'& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
		opacity: 1,
	},
	'& .MuiDataGrid-columnSeparator--resizable': {
		cursor: 'col-resize',
	},
	'& .MuiDataGrid-columnHeader': {
		borderRight: 'none',
	},
	'& .MuiDataGrid-cell': {
		fontFamily: 'Poppins, sans-serif',
		fontSize: '12px',
		color: '#092056',
		borderBottom: 'none',
		borderRight: 'none',
	},
	'& .MuiDataGrid-row': {
		borderBottom: 'none',
	},
	'& .MuiDataGrid-row:hover': {
		backgroundColor: 'rgba(0, 165, 255, 0.06)',
	},
	'& .MuiCheckbox-root': {
		color: '#8695AF',
		'&.Mui-checked': {
			color: '#00A5FF',
		},
	},
	'& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
		outline: 'none',
	},
}

const defaultTimesheets: TimesheetNoticeRow[] = [
	{
		id: 'ts-1',
		motorista: 'Alexandre Gomes',
		nr: '0137',
		dataInicio: '02/05/2024',
		dataFim: '02/05/2024',
		horaInicio: '08:00',
		horaFim: '17:00',
		filial: 'Transol',
		servico: 'Regular',
		email: 'alexandre@email.com',
		zona: '1',
		estado: 'OK',
	},
	{
		id: 'ts-2',
		motorista: 'Ana Martins',
		nr: '0210',
		dataInicio: '02/05/2024',
		dataFim: '02/05/2024',
		horaInicio: '09:00',
		horaFim: '18:00',
		filial: 'Transol',
		servico: 'Turismo',
		email: 'ana@email.com',
		zona: '2',
		estado: 'OK',
	},
	{
		id: 'ts-3',
		motorista: 'Rui Silva',
		nr: '0182',
		dataInicio: '02/05/2024',
		dataFim: '02/05/2024',
		horaInicio: '07:30',
		horaFim: '16:00',
		filial: 'Transol',
		servico: 'Transfer',
		email: 'rui@email.com',
		zona: '3',
		estado: 'OK',
	},
]

const defaultWarnings: TimesheetNoticeRow[] = [
	{
		id: 'fw-1',
		motorista: 'Beatriz Silva',
		ext: '0001',
		nr: '0001',
		nrMec: '00001',
		idMot: '00001',
		dataEscala: '02/05/2024',
		idZona: '1',
		zona: 'Portimão',
		email: 'beatriz@email.com',
		telefone: '910000000',
		descricao: 'Folga Semanal',
	},
	{
		id: 'fw-2',
		motorista: 'Carlos Pereira',
		ext: '0002',
		nr: '0002',
		nrMec: '00002',
		idMot: '00002',
		dataEscala: '02/05/2024',
		idZona: '1',
		zona: 'Portimão',
		email: 'carlos@email.com',
		telefone: '910000000',
		descricao: 'Folga Semanal',
	},
	{
		id: 'fw-3',
		motorista: 'Diana Costa',
		ext: '0003',
		nr: '0003',
		nrMec: '00003',
		idMot: '00003',
		dataEscala: '02/05/2024',
		idZona: '1',
		zona: 'Portimão',
		email: 'diana@email.com',
		telefone: '910000000',
		descricao: 'Complementar',
	},
]

const driverColumn: GridColDef<TimesheetNoticeRow> = {
	field: 'motorista',
	headerName: 'Motorista',
	width: 200,
	renderCell: params => (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
			<Avatar
				src={params.row.avatarUrl}
				sx={{
					borderRadius: '100%',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					bgcolor: 'lightgrey 50% / cover no-repeat',
					width: 32,
					height: 32,
					fontSize: '20px',
					fontWeight: 400,
					lineHeight: 1,
				}}
			>
				{params.value?.[0] ?? 'M'}
			</Avatar>
			<Typography sx={{ fontSize: '12px', color: '#092056' }}>{params.value}</Typography>
		</Box>
	),
}

const CustomModalTimesheetNotices: React.FC = () => {
	const { isOpen, closeModal, modalData, modalHandlers } = useModal()
	const data = modalData as TimesheetNoticesModalData | undefined
	const [handlePrint, handleSend] = modalHandlers ?? []
	const [tabValue, setTabValue] = useState(0)
	const [density, setDensity] = useState<DensityMode>('compact')
	const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([])
	const [differentHour, setDifferentHour] = useState(false)
	const [hourValue, setHourValue] = useState('')
	const [includeExternal, setIncludeExternal] = useState(true)
	const [includeInternal, setIncludeInternal] = useState(true)
	const [informRest, setInformRest] = useState(true)

	const timesheetRows = useMemo(() => (data?.timesheets?.length ? data.timesheets : defaultTimesheets), [data])
	const warningRows = useMemo(() => (data?.lateWarnings?.length ? data.lateWarnings : defaultWarnings), [data])

	const rows = tabValue === 0 ? timesheetRows : warningRows

	const timesheetColumns = useMemo<GridColDef<TimesheetNoticeRow>[]>(
		() => [
			driverColumn,
			{ field: 'nr', headerName: 'Nr', width: 63 },
			{ field: 'dataInicio', headerName: 'Data Início', width: 70 },
			{ field: 'dataFim', headerName: 'Data Fim', width: 70 },
			{ field: 'horaInicio', headerName: 'Hora Início', width: 70 },
			{ field: 'horaFim', headerName: 'Hora Fim', width: 88 },
			{ field: 'filial', headerName: 'Filial', width: 79 },
			{ field: 'servico', headerName: 'Serviço', width: 87 },
			{ field: 'email', headerName: 'Email', width: 167 },
			{ field: 'zona', headerName: 'Zona', width: 54 },
			{ field: 'estado', headerName: 'Estado', width: 54 },
		],
		[],
	)

	const warningColumns = useMemo<GridColDef<TimesheetNoticeRow>[]>(
		() => [
			driverColumn,
			{ field: 'ext', headerName: 'Ext', width: 63 },
			{ field: 'nr', headerName: 'Nr', width: 70 },
			{ field: 'nrMec', headerName: 'Nr Mec', width: 70 },
			{ field: 'idMot', headerName: 'ID Mot', width: 70 },
			{ field: 'dataEscala', headerName: 'Data Escala', width: 106 },
			{ field: 'idZona', headerName: 'ID Zona', width: 79 },
			{ field: 'zona', headerName: 'Zona', width: 87 },
			{ field: 'email', headerName: 'Email', width: 160 },
			{ field: 'telefone', headerName: 'Telefone', width: 87 },
			{ field: 'descricao', headerName: 'Descrição', width: 122 },
		],
		[],
	)

	const columns = tabValue === 0 ? timesheetColumns : warningColumns

	if (!data) {
		return null
	}

	const totalLabel = tabValue === 0 ? 'Folhas de Ponto' : 'Avisos de Folga Atrasada'
	const referenceDate = (tabValue === 0 ? timesheetRows[0]?.dataInicio : warningRows[0]?.dataEscala) ?? '02/05/2024'
	const totalCount =
		(tabValue === 0 ? data?.timesheets?.length : data?.lateWarnings?.length) || (tabValue === 0 ? 90 : 40)
	const headerLabel = `${totalCount} ${totalLabel} de ${referenceDate}`
	const selectedCount = selectionModel.length

	const handlePrintAction = () => {
		if (typeof handlePrint === 'function') {
			handlePrint(rows)
			return
		}
		console.log('Imprimir folhas de ponto', rows)
	}

	const handleSendAction = () => {
		if (typeof handleSend === 'function') {
			handleSend(rows)
			return
		}
		console.log('Enviar folhas de ponto', rows)
	}

	return (
		<Dialog
			open={isOpen}
			onClose={closeModal}
			maxWidth={false}
			sx={{ pointerEvents: 'auto', zIndex: 2000 }}
			PaperProps={{
				sx: {
					width: 'min(1200px, 96vw)',
					height: '660px',
					borderRadius: '6px',
					backgroundColor: '#FFFFFF',
					boxShadow: dialogShadow,
					overflow: 'hidden',
					zIndex: 2001,
				},
			}}
		>
			<Box sx={{ fontFamily: 'Poppins, sans-serif', color: '#092056', height: '100%' }}>
				<Box sx={{ padding: '16px 24px' }}>
					<Typography
						sx={{
							fontSize: '16px',
							fontWeight: 600,
							lineHeight: 1.75,
							letterSpacing: '0.15px',
							color: '#092056',
						}}
					>
						Folhas de Ponto e Avisos de Folga Atrasada
					</Typography>
				</Box>

				<Box
					sx={{
						padding: '24px',
						display: 'flex',
						flexDirection: 'column',
						gap: '16px',
						height: 'calc(100% - 112px)',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							gap: '16px',
							flexWrap: 'wrap',
						}}
					>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
							<FormControlLabel
								control={
									<Checkbox
										size='small'
										checked={differentHour}
										onChange={event => setDifferentHour(event.target.checked)}
										sx={checkboxSx}
									/>
								}
								label='Início / Fim diferente das 03:00'
								sx={checkboxLabelSx}
							/>
							<TextField
								label='Hora'
								value={hourValue}
								onChange={event => setHourValue(event.target.value)}
								size='small'
								placeholder='hh:mm'
								sx={{ ...timeFieldSx, width: '90px' }}
								InputLabelProps={{ shrink: true }}
							/>
						</Box>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: '16px',
								flexWrap: 'wrap',
								justifyContent: 'flex-end',
							}}
						>
							<FormControlLabel
								control={
									<Checkbox
										size='small'
										checked={includeExternal}
										onChange={event => setIncludeExternal(event.target.checked)}
										sx={checkboxSx}
									/>
								}
								label='Motoristas Externos'
								sx={checkboxLabelSx}
							/>
							<FormControlLabel
								control={
									<Checkbox
										size='small'
										checked={includeInternal}
										onChange={event => setIncludeInternal(event.target.checked)}
										sx={checkboxSx}
									/>
								}
								label='Motoristas da casa'
								sx={checkboxLabelSx}
							/>
							<FormControlLabel
								control={
									<Checkbox
										size='small'
										checked={informRest}
										onChange={event => setInformRest(event.target.checked)}
										sx={checkboxSx}
									/>
								}
								label='Informar Motoristas de Folga'
								sx={checkboxLabelSx}
							/>
						</Box>
					</Box>

					<Box sx={{ display: 'flex', justifyContent: 'center' }}>
						<Tabs
							value={tabValue}
							onChange={(_, value) => {
								setTabValue(value)
								setSelectionModel([])
							}}
							TabIndicatorProps={{
								style: {
									backgroundColor: '#00A5FF',
									height: '3px',
									borderRadius: '2px',
								},
							}}
							sx={{
								minHeight: '42px',
								'& .MuiTab-root': {
									fontFamily: 'Poppins, sans-serif',
									fontWeight: 600,
									fontSize: '13px',
									textTransform: 'none',
									color: '#8695AF',
									minHeight: '42px',
								},
								'& .Mui-selected': {
									color: '#00A5FF',
								},
							}}
						>
							<Tab label='Folhas de Ponto' />
							<Tab label='Folga Atrasada' />
						</Tabs>
					</Box>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							gap: '16px',
						}}
					>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
							<Typography
								sx={{
									fontSize: '24px',
									fontWeight: 600,
									lineHeight: 1.334,
									color: '#092056',
								}}
							>
								{headerLabel}
							</Typography>
							<Box
								sx={{
									width: '1px',
									height: '24px',
									backgroundColor: 'rgba(134, 149, 175, 0.4)',
								}}
							/>
							{selectedCount > 0 && (
								<Typography
									sx={{
										fontSize: '13px',
										fontWeight: 500,
										color: '#8695AF',
									}}
								>
									{selectedCount} selecionado(s)
								</Typography>
							)}
						</Box>
						<ToggleButtonGroup
							exclusive
							value={density}
							onChange={(_, value) => value && setDensity(value)}
							sx={toggleGroupSx}
						>
							<ToggleButton
								value='compact'
								aria-label='Compacto'
							>
								<TableRowsIcon sx={{ fontSize: '16px' }} />
							</ToggleButton>
							<ToggleButton
								value='standard'
								aria-label='Espaçado'
							>
								<ViewHeadlineIcon sx={{ fontSize: '16px' }} />
							</ToggleButton>
						</ToggleButtonGroup>
					</Box>

					<Box sx={{ flex: 1 }}>
						<Box sx={{ height: 322 }}>
							<AnimatedDataGridPro
								slots={{ columnMenu: CustomColumnMenu }}
								slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
								rows={rows}
								columns={columns}
								checkboxSelection
								disableRowSelectionOnClick
								density={density}
								rowSelectionModel={selectionModel}
								onRowSelectionModelChange={model => setSelectionModel(model)}
								rowHeight={density === 'compact' ? 32 : 40}
								columnHeaderHeight={54}
								pagination
								pageSizeOptions={[5, 10]}
								sx={{ ...tableSx, ...dataGridDensityTransitionSx }}
							/>
						</Box>
					</Box>
				</Box>

				<Box
					sx={{
						padding: '8px 24px 16px',
						display: 'flex',
						justifyContent: 'flex-end',
						gap: '8px',
					}}
				>
					<CustomButton
						variant='text'
						size='medium'
						onClick={closeModal}
						sx={popupSecondaryButtonSx}
					>
						Fechar
					</CustomButton>
					<CustomButton
						variant='text'
						size='medium'
						onClick={handlePrintAction}
						sx={popupSecondaryButtonSx}
					>
						Imprimir
					</CustomButton>
					<CustomButton
						variant='contained'
						size='medium'
						onClick={handleSendAction}
						sx={popupActionButtonSx}
					>
						Enviar
					</CustomButton>
				</Box>
			</Box>
		</Dialog>
	)
}

export default CustomModalTimesheetNotices
